//@flow
import {useCallback, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import {
  createBlogPostService,
  editBlogPostService,
  deleteBlogPostService,
  listBlogPostsService,
  getBlogPostService,
} from '../promises/blog-post';

import {actions} from '../duck';
import {useActions} from '../../../utils/globalStore';
import {uploadImageService} from '../../file-storage/promises';

import type {
  CreateBlogPost,
  EditBlogPost,
  BlogPostSection,
} from '../api/blog-post';
import type {RootState} from '../../';
import {BLOG_ERROR_MESSAGE_MAP, type BlogFormFields} from '../constants';
import type {BlogPostDetail} from '../../../entities';
import {useBlogAuthors} from './blog-author';
import {getAuthorService} from '../promises/blog-author';
import {stringToSafeLink} from '../../../utils/string';
import {useLocalStorage} from '../../../utils/dom';

const BLOG_FILE_PREFIX = 'ui/blog-post';

const blogPostsSelector = (state: RootState) => state.blog.blogPosts;
const blogPaginationSelector = (state: RootState) => state.blog.pagination;

export function useBlogPostPagination() {
  const pagination = useSelector(blogPaginationSelector);
  return pagination;
}

export function useInitializeBlogPosts() {
  const [loading, setLoading] = useState(false);
  const setSavedPosts = useActions(actions.setSavedBlogPosts);
  const setBlogPagination = useActions(actions.setBlogPagination);

  const posts = useSelector(blogPostsSelector);
  const pagination = useBlogPostPagination();
  const authors = useBlogAuthors();
  const filter = useBlogPostFilter();

  const authorsIds = authors ? authors.map(a => a.id).join(',') : '';
  const filterString = JSON.stringify(filter);

  const page = pagination.page;
  const size = pagination.size;

  useEffect(() => {
    setLoading(true);

    const getBlogPost = async () => {
      if (!authors || !authors.length) return;
      const {
        data: postList,
        pagination: paginationResponse,
      } = await listBlogPostsService(filter, {page, size}).catch(() => ({
        data: [],
        pagination: {
          page: 0,
          size: 10,
          total: 0,
        },
      }));

      const postsWithAuthors = postList.map(post => {
        const author = authors.find(author => author.id === post.author.id);

        return {
          ...post,
          author: author ? author.name : '',
        };
      });

      setSavedPosts(postsWithAuthors);
      if (paginationResponse) {
        setBlogPagination(paginationResponse);
      }
    };
    getBlogPost()
      .catch(error => {
        setSavedPosts([]);
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorsIds, filterString, page, size, setSavedPosts, setBlogPagination]);

  return {
    loading,
    posts,
  };
}

export function useBlogPostList() {
  const posts = useSelector(blogPostsSelector);

  return posts || [];
}

const currentBlogPostActions = {
  set: actions.setCurrentBlogPost,
  clear: actions.clearCurrentBlogPost,
};

const currentBlogPostSelector = (state: RootState) =>
  state.blog.currentBlogPost;

export function useCurrentBlogPost(): BlogPostDetail | null {
  const currentBlogPost = useSelector(currentBlogPostSelector);

  return currentBlogPost;
}

export function useBlogPost(
  id?: string
): {
  loading: boolean,
  post: BlogPostDetail | null,
} {
  const [loading, setLoading] = useState(false);
  const post = useCurrentBlogPost();

  const currentBlogActions = useActions(currentBlogPostActions);

  useEffect(() => {
    setLoading(true);
    if (!id || id === 'new') return;
    const getBlogPost = async () => {
      const post = await getBlogPostService(id);
      const author = await getAuthorService(post.author.id);

      currentBlogActions.set({
        ...post,
        author,
      });
    };

    getBlogPost()
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      currentBlogActions.clear();
    };
  }, [currentBlogActions, id]);

  return {
    loading,
    post,
  };
}

const blogPostActions = {
  create: actions.addBlogPost,
  update: actions.updateBlogPost,
  delete: actions.deleteBlogPost,
  togglePublish: actions.toggleBlogPostPublish,
  toggleFeatured: actions.toggleBlogPostFeatured,
};

export function useBlogPostActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    create,
    update,
    delete: deletePost,
    togglePublish,
    toggleFeatured,
  } = useActions(blogPostActions);

  const handleError = useCallback(
    (e: any) => {
      console.error(e);
      const errorMessage =
        BLOG_ERROR_MESSAGE_MAP[e?.error?.error] ??
        BLOG_ERROR_MESSAGE_MAP[e?.error?.message];
      setError(
        errorMessage ||
          'Ocurrió un error al crear el post. Por favor, intenta nuevamente.'
      );
    },
    [setError]
  );

  useEffect(() => {
    if (!error) return;

    const timeout = setTimeout(() => {
      setError(null);
    }, 5000);

    return () => {
      clearTimeout(timeout);
    };
  }, [error]);

  const createPost = async (post: BlogFormFields) => {
    try {
      setLoading(true);

      const categories =
        post.categories?.map(category => category.id) || undefined;
      const files = [post.poster];

      //$FlowFixMe
      const filesResponse = await uploadImageService(files, BLOG_FILE_PREFIX);

      const [mainImage] = filesResponse;

      const sections: BlogPostSection[] = [];

      for (const postSection of post.content) {
        const section: BlogPostSection = {
          content: postSection.text,
          title: postSection.subtitle,
          enabled: true,
        };

        if (Array.isArray(postSection.images)) {
          for (let index = 0; index < postSection.images.length; index++) {
            const element = postSection.images[index];

            const imageForm = element.image;
            if (!imageForm) continue;
            if (typeof imageForm === 'string') {
              section[`image${index + 1}Id`] = imageForm;
              section[`image${index + 1}Description`] = element.alt;
              continue;
            }

            const [image] = await uploadImageService(
              //$FlowFixMe
              [imageForm],
              BLOG_FILE_PREFIX
            );

            section[`image${index + 1}Id`] = image.id;
            section[`image${index + 1}Description`] = element.alt;
          }
        }

        sections.push(section);
      }

      const postRequest: CreateBlogPost = {
        title: post.title,
        enabled: !!post.enabled,
        categories,
        author: {
          name: post.author,
        },
        seoContent: post.seoDescription || undefined,
        seoTitle: post.seoTitle || undefined,
        isCustomSlug: post.urlIsCustom,
        slug: post.urlIsCustom ? post.url : stringToSafeLink(post.title),
        mainImageDescription: post.posterAlt || undefined,
        mainImageId: mainImage.id,
        relatedEntities: post.relatedEntities,
        sections,
        enabledRelatedEntities: post.enabledRelatedEntities,
        featured: !!post.postFeatured,
      };

      const data = await createBlogPostService(postRequest);
      create({
        ...data,
        author: post.author,
        categories: post.categories || [],
      });
      return true;
    } catch (e) {
      handleError(e);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const updatePost = async (id: string, post: BlogFormFields) => {
    try {
      setLoading(true);
      const categories =
        post.categories?.map(category => category.id) || undefined;

      const files = typeof post.poster === 'string' ? [] : [post.poster];
      let mainImageId =
        typeof post.poster === 'string' ? post.poster : undefined;

      if (files.length) {
        //$FlowFixMe
        const filesResponse = await uploadImageService(files, BLOG_FILE_PREFIX);
        const [mainImage] = filesResponse;
        mainImageId = mainImage.id;
      }

      const sections: BlogPostSection[] = [];

      for (const postSection of post.content) {
        const section: BlogPostSection = {
          content: postSection.text,
          title: postSection.subtitle,
          enabled: true,
        };

        if (Array.isArray(postSection.images)) {
          for (let index = 0; index < postSection.images.length; index++) {
            const element = postSection.images[index];

            const imageForm = element.image;
            if (!imageForm) continue;
            if (typeof imageForm === 'string') {
              section[`image${index + 1}Id`] = imageForm;
              section[`image${index + 1}Description`] =
                element.alt || undefined;
              continue;
            }

            const [image] = await uploadImageService(
              //$FlowFixMe
              [imageForm],
              BLOG_FILE_PREFIX
            );

            section[`image${index + 1}Id`] = image.id;
            section[`image${index + 1}Description`] = element.alt || undefined;
          }
        }

        sections.push(section);
      }

      const postRequest: EditBlogPost = {
        title: post.title,
        enabled: !!post.enabled,
        categories,
        author: {
          name: post.author,
        },
        seoContent: post.seoDescription,
        seoTitle: post.seoTitle,
        isCustomSlug: post.urlIsCustom,
        slug: post.urlIsCustom ? post.url : stringToSafeLink(post.title),
        mainImageDescription: post.posterAlt || undefined,
        mainImageId,
        relatedEntities: post.relatedEntities,
        enabledRelatedEntities: post.enabledRelatedEntities,
        featured: !!post.postFeatured,
        sections,
      };

      const data = await editBlogPostService(id, postRequest);
      update({
        ...data,
        categories: post.categories || [],
        author: post.author,
      });

      return true;
    } catch (e) {
      handleError(e);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const deleteBlogPost = (id: string) => {
    setLoading(true);
    return deleteBlogPostService(id)
      .then(() => {
        deletePost(id);
      })
      .catch(() => {
        setError(
          'Ocurrió un error al eliminar el post. Por favor, intenta nuevamente.'
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const toggleBlogPostPublish = (id: string, publish: boolean) => {
    setLoading(true);
    return editBlogPostService(id, {enabled: publish})
      .then(post => {
        togglePublish(id);
      })
      .catch(error => {
        handleError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const togglePostFeatured = (id: string, featured: boolean) => {
    setLoading(true);
    return editBlogPostService(id, {featured})
      .then(post => {
        toggleFeatured(id);
      })
      .catch(error => {
        handleError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return {
    createPost,
    updatePost,
    deleteBlogPost,
    togglePostFeatured,
    toggleBlogPostPublish,
    loading,
    error,
  };
}
const blogFilterSelector = (state: RootState) => state.blog.blogPostFilter;

export function useBlogPostFilter() {
  const filter = useSelector(blogFilterSelector);

  return filter;
}

const blogFilterActions = {
  setSearch: actions.setSearchBlogPostFilter,
  setEnabled: actions.setEnabledBlogPostFilter,
  setAuthors: actions.setAuthorsBlogPostFilter,
  setCategories: actions.setCategoriesBlogPostFilter,
};

export function useBlogPostFilterActions() {
  const {setSearch, setEnabled, setAuthors, setCategories} = useActions(
    blogFilterActions
  );

  const setBlogPostSearch = useCallback(
    (search: string) => {
      setSearch(search);
    },
    [setSearch]
  );

  const setBlogPostEnabled = (enabled: boolean) => {
    setEnabled(enabled);
  };

  const setBlogPostAuthors = (authors: string[]) => {
    setAuthors(authors);
  };

  const setBlogPostCategories = (categories: string[]) => {
    setCategories(categories);
  };

  return {
    setBlogPostSearch,
    setBlogPostEnabled,
    setBlogPostAuthors,
    setBlogPostCategories,
  };
}

const paginationActions = {
  setPagination: actions.setBlogPagination,
};

export function useBlogPostPaginationActions() {
  const {setPagination} = useActions(paginationActions);
  const pagination = useBlogPostPagination();

  const setPage = (page: number) => {
    setPagination({
      ...pagination,
      page,
    });
  };

  const setSize = (size: number) => {
    setPagination({
      ...pagination,
      size,
    });
  };

  return {
    setPage,
    setSize,
  };
}

const FORM_DRAFT_VALUES_KEY = 'new-blog-post-draft';
export function useBlogFormDraftValue() {
  const [draft, setDraft] = useLocalStorage<BlogFormFields | null>(
    FORM_DRAFT_VALUES_KEY,
    null
  );

  const updateDraft = useCallback(
    (data: any) => {
      setDraft({
        ...draft,
        ...data,
      });
    },
    [draft, setDraft]
  );

  const clearDraft = useCallback(() => {
    setDraft(null);
  }, [setDraft]);

  return {
    draft,
    updateDraft,
    clearDraft,
  };
}
